import React, { createContext } from 'react';
import isEqual from 'lodash/isEqual';
import memoizeOne from 'memoize-one';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { EditorActions } from '@atlaskit/editor-core';

import type { PublishProps } from '@confluence/editor-features/entry-points/types.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { CollabEditState } from '@confluence/native-collab/entry-points/commonCollabTypes.ts';
import type { RetrieveEditorTitleAndContent } from '@confluence/editor-features/entry-points/useRetrieveEditorTitleAndContent.ts';

export type PrimaryToolbarContextValue = {
	forceDisabled?: boolean;
	userOffline: boolean;
	collabLimitReached: boolean;
	contentId: string;
	contentType: string;
	isContentCreationPanelOpen: boolean;
	isUnpublishedDraft: boolean;
	onClickPublish: (arg: PublishProps) => () => void;
	onClickOpenContentCreationPanel: () => void;
	onPreviewClicked: (...args: any[]) => void;
	saveDraft: (arg?: any) => any;
	spaceKey: string;
	title: string | null;
	showRevertDialog: () => void;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	isPublishingContent: boolean;
	editorActions: EditorActions;
	isSingleUserSession: boolean;
	navigateOnClose: () => void;
	triggerAnalyticsEvent: (arg: string, extraData?: { [key: string]: any }) => void;
	shouldRenderPreview: boolean;
	shouldShowRecentSpotlight: boolean;
	checkIfConditionPreventingPublish?: () => Promise<boolean>;
	flags: FlagsStateContainer;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	disabled: boolean;
	isTitleFocused: boolean | null;
	collabEditState: CollabEditState;
	hasEditPermission?: boolean;
	ellipsisEditorActions?: {
		canUndo?: boolean;
		canRedo?: boolean;
		undo?: () => boolean;
		redo?: () => boolean;
		activateFindReplace?: () => boolean;
	};
};

export type PrimaryToolbarContextProviderProps = {
	value: PrimaryToolbarContextValue;
	children?: React.ReactNode;
};

const PrimaryToolbarContext = createContext<PrimaryToolbarContextValue | null>(null);

const memoizeDeepEq = memoizeOne(<T,>(obj: T) => obj, isEqual);

export function PrimaryToolbarContextProvider({
	children,
	value,
}: PrimaryToolbarContextProviderProps): React.JSX.Element {
	const memoizedValue = memoizeDeepEq(value);
	return (
		<PrimaryToolbarContext.Provider value={memoizedValue as PrimaryToolbarContextValue}>
			{children}
		</PrimaryToolbarContext.Provider>
	);
}

export function usePrimaryToolbarContext(): PrimaryToolbarContextValue {
	const context = React.useContext(PrimaryToolbarContext);
	if (!context) {
		throw new Error('usePrimaryToolbarContext must be used within a PrimaryToolbarContextProvider');
	}
	return context;
}
